import React, { useMemo, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import Popup from 'reactjs-popup';
import { centsToDollarsNumber, getLocalizedPricing } from '../../lib/util';
import * as types from '../../types';
import Button from '../Button';
import styles from './SubscriptionUpsellModal.module.scss';
import { Image } from '../Image';
import * as events from '../../lib/analytics/events';
import { useDispatch, useSelector } from 'react-redux';
import { applyCouponToCart, isValidCodeResult } from '../../lib/promoCode';
import { cartActions as storeCartActions } from '../../reducers/storeCart';

import Check from '../../assets/images/icons/check_green.svg';
import CollarBlue1 from '../../assets/images/collar_hero_blue_1.png';
import CollarYellow1 from '../../assets/images/collar_hero_yellow_1.png';
import CollarGray1 from '../../assets/images/collar_hero_gray_1.png';
import CollarPink1 from '../../assets/images/collar_hero_pink_1.png';
import Mini1 from '../../assets/images/collar_hero_mini_1.png';
import { getFiGrowthBook } from '../../lib/growthbook';
import { useLocale } from '../../contexts/LocaleContext';

interface SubscriptionUpsellModalProps {
  onAccept: () => void;
  onClose: () => void;
  onIgnore: () => void;
  open: boolean;
  selectedSubscription: types.ISubscriptionProduct;
  subscriptionToUpsell: types.ISubscriptionProduct;
  title?: string;
  collarColor?: types.ColorOption;
}

function subscriptionName(subscriptionProduct: types.ISubscriptionProduct, t: TFunction) {
  if (subscriptionProduct.renewalMonths === 1) {
    return t('subscription_monthly');
  }

  return t('subscription_month', { months: subscriptionProduct.renewalMonths });
}

function subscriptionNameForCTA(subscriptionProduct: types.ISubscriptionProduct, t: TFunction) {
  if (subscriptionProduct.renewalMonths === 1) {
    return t('subscription_month_to_month');
  }

  return t('subscription_months', { months: subscriptionProduct.renewalMonths });
}

function determineImageUrl(subscriptionToUpsell: types.ISubscriptionProduct, collarColor: types.ColorOption) {
  // Mini
  if (subscriptionToUpsell.canBePurchasedForProductIds.includes(types.miniCollarId)) {
    return Mini1;
  }

  // S3+
  switch (collarColor) {
    case types.ColorOption.Gray:
      return CollarGray1;
    case types.ColorOption.Pink:
      return CollarPink1;
    case types.ColorOption.Blue:
      return CollarBlue1;
    case types.ColorOption.Yellow:
      return CollarYellow1;
    default:
      return CollarBlue1;
  }
}

export default function SubscriptionUpsellModal({
  onAccept,
  onClose,
  onIgnore,
  open,
  selectedSubscription,
  subscriptionToUpsell,
  title,
  collarColor = types.ColorOption.Blue,
}: SubscriptionUpsellModalProps) {
  const { t } = useTranslation();
  const analyticsEventProperties: events.SubscriptionEventProps = useMemo(
    () => ({
      priceInCents: subscriptionToUpsell.priceInCents,
      productId: subscriptionToUpsell.id,
      productName: subscriptionToUpsell.name,
      sku: subscriptionToUpsell.sku,
    }),
    [subscriptionToUpsell],
  );

  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const dispatch = useDispatch();
  const { currency, wholeNumberCurrencyFormatter } = useLocale();

  useEffect(() => {
    if (open) {
      events.subscriptionUpsell.viewed(analyticsEventProperties);
    }
  }, [analyticsEventProperties, open]);

  // This is a rare situation where we want the default value to be true,
  // because we're experimenting with *removing* the waiver
  const waiveActivationFee = getFiGrowthBook().getFeatureValue<boolean>(
    'ecom-3-month-upsell-waive-activation-fee',
    true,
  );

  const imageUrl = determineImageUrl(subscriptionToUpsell, collarColor);

  const subscriptionToUpsellPriceInCents = getLocalizedPricing(subscriptionToUpsell.pricingInCents, currency);
  const subscriptionToUpsellActivationFeeInCents = subscriptionToUpsell.activationFeePricingInCents
    ? getLocalizedPricing(subscriptionToUpsell.activationFeePricingInCents, currency)
    : 0;
  const selectedSubscriptionPriceInCents = getLocalizedPricing(selectedSubscription.pricingInCents, currency);
  const selectedSubscriptionActivationFeeInCents = selectedSubscription.activationFeePricingInCents
    ? getLocalizedPricing(selectedSubscription.activationFeePricingInCents, currency)
    : 0;

  const upsellCostOverYearInCents =
    (subscriptionToUpsellPriceInCents / subscriptionToUpsell.renewalMonths) * 12 +
    (subscriptionToUpsellActivationFeeInCents ?? 0);
  const currentCostOverYearInCents =
    selectedSubscriptionPriceInCents * 12 + (selectedSubscriptionActivationFeeInCents ?? 0);
  const currentCostOverUpsellTermInCents =
    selectedSubscriptionPriceInCents * subscriptionToUpsell.renewalMonths +
    (selectedSubscriptionActivationFeeInCents ?? 0);

  const featuredPriceInCents = subscriptionToUpsellPriceInCents;
  const crossedOutPriceInCents = currentCostOverUpsellTermInCents;

  const yearlySavingsInCents = currentCostOverYearInCents - upsellCostOverYearInCents;

  // Calculate savings percentage dynamically based on crossedOutPrice vs featuredPrice
  const savingsPercentage = Math.round(
    ((crossedOutPriceInCents - featuredPriceInCents) / crossedOutPriceInCents) * 100,
  );

  // Set the title dynamically if not provided
  if (!title) {
    if (!waiveActivationFee) {
      const savingsString = wholeNumberCurrencyFormatter.format(centsToDollarsNumber(yearlySavingsInCents));
      title = `Prepay Your Membership and Save ${savingsString}/yr`;
    } else {
      title = t('prepay_your_membership_and_save_percent', { percent: savingsPercentage });
    }
  }

  return (
    <Popup className="s3-popup" onClose={onClose} open={open} closeOnDocumentClick={false}>
      <div className={styles.container}>
        <div className={styles.headerSection}>
          <h1>{title}</h1>
          <div className={styles.offerContainer}>
            <div className={styles.specialOfferChipSmall}>
              <p>{t('special_offer')}</p>
            </div>
            <div className={styles.offerDetailsContainer}>
              <p className={styles.upsellSubscriptionTermText}>{subscriptionName(subscriptionToUpsell, t)}</p>
              <div>
                <div className={styles.pricesContainer}>
                  <p className={styles.upsellSubscriptionPriceText}>
                    {wholeNumberCurrencyFormatter.format(centsToDollarsNumber(featuredPriceInCents))}
                  </p>
                  {waiveActivationFee ? (
                    <p className={styles.selectedSubscriptionPriceText}>
                      {wholeNumberCurrencyFormatter.format(centsToDollarsNumber(crossedOutPriceInCents))}
                    </p>
                  ) : (
                    <></>
                  )}
                </div>
                {waiveActivationFee ? (
                  <p className={styles.upgradeIncentive}>
                    <Trans
                      i18nKey="activation_fee_amount_waived_markup"
                      components={{ price: <span /> }}
                      values={{
                        amount: wholeNumberCurrencyFormatter.format(
                          centsToDollarsNumber(selectedSubscriptionActivationFeeInCents ?? 0),
                        ),
                      }}
                    />
                  </p>
                ) : (
                  <></>
                )}
              </div>
              <p className={styles.commitment}>{t('six_months_minimum')}</p>
            </div>
            <Image image={{ type: 'image', url: Check }} />
          </div>
        </div>
        <div className={styles.heroImageSection}>
          <Image image={{ type: 'image', url: imageUrl }} />
        </div>
        <div className={styles.ctaSection}>
          <div className={styles.ctaButtons}>
            <Button
              className={styles.ctaPrimary}
              onClick={() => {
                events.subscriptionUpsell.acceptedUpsell(analyticsEventProperties);
                onAccept();

                if (waiveActivationFee) {
                  /**
                   * TODO: if/when we confirm we want to apply the discount code, this should be reworked
                   * and probably pulled up into the actual `onAccept` function itself.
                   * We may also be fully removing the activation fee from all 3 month subscriptions,
                   * in which case the coupon wouldn't be necessary anymore.
                   */
                  const couponCode = '3MO_WAIVE_ACTIVATION';
                  applyCouponToCart(cart, couponCode).then((couponResult) => {
                    if (isValidCodeResult(couponResult)) {
                      dispatch(storeCartActions.addCoupon(couponCode));
                    }
                  });
                }
              }}
            >
              {t('switch_to_subscription', { subscription: subscriptionNameForCTA(subscriptionToUpsell, t) })}
            </Button>
            <Button
              onClick={() => {
                events.subscriptionUpsell.ignoredUpsell(analyticsEventProperties);
                onIgnore();
              }}
              secondary
            >
              {t('keep_subscription', { subscription: subscriptionNameForCTA(selectedSubscription, t) })}
            </Button>
          </div>
        </div>
      </div>
    </Popup>
  );
}
